// deps
import React, { useEffect, useMemo, useRef } from "react";

// internal-deps
import { ScreenReaderOnly, useEffectOnce } from "@swiggy-private/core-ui";
import { useSavingsBannerAnimation } from "@swiggy-private/checkout-core";

// utils
import { formatForA11y } from "@super-ui/helpers/String";

// constants
import { CART_HEADER_DATA_TEST_IDS } from "../../test-ids/savings-banner";
import { SAVINGS_BANNER_ANIMATION_DURATION } from "./constants";

//styles
import { IconWrapper, SavingsBannerOnelineBlackContainer } from "./styles/CartSavingsBannerOneline.style";

// components
import CartSavingsOneLineBlackTextContainer from "./components/CartSavingsOneLineBlackTextContainer";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SAVINGS_ICON_DIMENSION } from "./CartSavingsBannerOneLineV2";
import { isEmpty } from "@swiggy-private/js-utils";

export const SAVINGS_ICON_PATH = "BLCK/Cart/BLCK_star.png";
export const BOLT_ICON_PATH = "portal/static-assets/hcms/bolt_filled.png";

export interface CartSavingsBannerOneLineBlackProps {
    title: string;
    subtitle: string;
    details: Record<string, number>;
    cssClass?: string;
    pauseUpdate?: boolean;
    onImpression?: VoidFunction;
    onUpdate?: VoidFunction;
    isExpress?: boolean;
    isOTG?: boolean;
    showFinalTheme?: boolean;
    isBolt?: boolean;
    is99Store?: boolean;
    isEOCart?: boolean;
    savingsBannerGradient?: string;
    savingsShimmerGradient?: string;
}

const CartSavingsBannerOneLineBlack: React.FC<CartSavingsBannerOneLineBlackProps> = ({
    title,
    subtitle,
    details,
    onImpression,
    onUpdate,
    isExpress,
    isOTG,
    showFinalTheme,
    isBolt,
    is99Store,
    isEOCart,
    savingsBannerGradient,
    savingsShimmerGradient,
}) => {
    const shouldRegister = !!(title || subtitle);
    const { shouldPlayAnimation, onAnimationComplete } = useSavingsBannerAnimation({
        shouldRegister,
    });

    const hasShimmerStartedRef = useRef(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        onUpdate?.();
    }, [title, subtitle, details, onUpdate]);

    useEffectOnce(() => {
        onImpression?.();
    });

    // Trigger onAnimationComplete after shimmer animation finishes
    useEffect(() => {
        if (shouldPlayAnimation && !hasShimmerStartedRef.current) {
            hasShimmerStartedRef.current = true;

            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                onAnimationComplete();
                timerRef.current = null;
            }, SAVINGS_BANNER_ANIMATION_DURATION);

            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
            };
        }
    }, [shouldPlayAnimation, onAnimationComplete]);

    const containerTestId = useMemo(() => {
        if (isBolt) {
            return CART_HEADER_DATA_TEST_IDS.BOLT_SAVING_STRIP;
        } else if (isEOCart) {
            return CART_HEADER_DATA_TEST_IDS.EO_SAVING_STRIP;
        } else if (is99Store) {
            return CART_HEADER_DATA_TEST_IDS._99STORE_SAVING_STRIP;
        }
        return CART_HEADER_DATA_TEST_IDS.BLCK_SAVING_STRIP;
    }, [isBolt, isEOCart, is99Store]);

    return (
        <SavingsBannerOnelineBlackContainer
            is99Store={is99Store}
            isEOCart={isEOCart}
            isBolt={isBolt}
            savingsBannerGradient={savingsBannerGradient}
            savingsShimmerGradient={savingsShimmerGradient}
            shouldPlayAnimation={shouldPlayAnimation}
            data-testid={containerTestId}
        >
            {!is99Store && !isEOCart && (
                <IconWrapper>
                    <Image
                        cloudinaryId={isBolt && isEmpty(title) ? BOLT_ICON_PATH : SAVINGS_ICON_PATH}
                        alt=""
                        width={SAVINGS_ICON_DIMENSION}
                        height={SAVINGS_ICON_DIMENSION}
                    />
                </IconWrapper>
            )}
            <CartSavingsOneLineBlackTextContainer
                title={title}
                subtitle={subtitle}
                details={details}
                isExpress={isExpress}
                isOTG={isOTG}
                showFinalTheme={showFinalTheme}
                isBolt={isBolt}
                is99Store={is99Store}
                isEOCart={isEOCart}
            />
            <ScreenReaderOnly text={formatForA11y(title, subtitle, details)} />
        </SavingsBannerOnelineBlackContainer>
    );
};

export default React.memo(CartSavingsBannerOneLineBlack, (_, nextProps) => !!nextProps?.pauseUpdate);
